import { QueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api';

/**
 * Shared query client.
 *
 * Defaults lean on the server being the source of truth for freshness: list data
 * is cheap to refetch and the WebSocket pushes the urgent changes, so a short
 * stale window plus refetch-on-focus is enough. Auth failures are never retried
 * because the answer will not change without a new login.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // 4xx will keep failing; only transient/server errors are worth a retry.
          if (error.status < 500) return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      retry: false,
    },
  },
});

/** Query keys in one place so invalidation from the socket layer stays correct. */
export const queryKeys = {
  bootstrap: ['bootstrap'] as const,

  conferences: {
    all: ['conferences'] as const,
    list: (params: Record<string, unknown>) => ['conferences', 'list', params] as const,
    stats: (params: Record<string, unknown>) => ['conferences', 'stats', params] as const,
    options: ['conferences', 'options'] as const,
    savedViews: (params: Record<string, unknown>) => ['conferences', 'saved-views', params] as const,
    detail: (pk: number | string) => ['conferences', 'detail', String(pk)] as const,
    tab: (pk: number | string, tab: string) => ['conferences', 'detail', String(pk), tab] as const,
  },

  registry: ['registry'] as const,
  modelData: (label: string, params: Record<string, unknown>) =>
    ['model-data', label, params] as const,
  modelObject: (label: string, pk: string | number) => ['model-object', label, String(pk)] as const,

  dashboard: (params: Record<string, unknown>) => ['dashboard', params] as const,
  // Deliberately not under the 'dashboard' prefix: the socket layer invalidates
  // that on every KPI tick, which would refetch the layout under an editor.
  dashboardLayout: ['dashboard-layout'] as const,

  users: {
    all: ['users'] as const,
    list: (params: Record<string, unknown>) => ['users', 'list', params] as const,
    detail: (pk: number | string) => ['users', 'detail', String(pk)] as const,
    phoneNumbers: (pk: number | string) => ['users', 'phone-numbers', String(pk)] as const,
    accessControl: (pk: number | string) => ['users', 'access-control', String(pk)] as const,
    performance: (pk: number | string, params: Record<string, unknown>) =>
      ['users', 'performance', String(pk), params] as const,
  },

  departments: ['departments'] as const,

  config: {
    all: ['config'] as const,
    /** Overview summary card — light payload from `/config/summary/`. */
    summary: ['config', 'summary'] as const,
    /** Full All-settings page from `/config/`. */
    business: ['config', 'business'] as const,
    aiRuntime: (params: Record<string, unknown>) => ['config', 'ai-runtime', params] as const,
    anthropicRouter: ['config', 'anthropic-router'] as const,
    cliproxyAccounts: ['config', 'cliproxy-accounts'] as const,
    voiceAgent: ['config', 'voice-agent'] as const,
    voices: (params: Record<string, unknown>) => ['config', 'voices', params] as const,
    runtime: ['config', 'runtime'] as const,
  },

  ops: {
    all: ['ops'] as const,
    index: ['ops', 'index'] as const,
    list: (key: string, params: Record<string, unknown>) => ['ops', key, 'list', params] as const,
    detail: (key: string, pk: number | string) => ['ops', key, 'detail', String(pk)] as const,
  },

  storage: (prefix: string) => ['storage', prefix] as const,

  memory: {
    all: ['memory'] as const,
    list: (model: string, params: Record<string, unknown>) => ['memory', model, params] as const,
    impact: (params: Record<string, unknown>) => ['memory', 'impact', params] as const,
  },

  evaluation: {
    weightMatrix: ['evaluation', 'weight-matrix'] as const,
    categorization: (params: Record<string, unknown> = {}) =>
      ['evaluation', 'categorization', params] as const,
    catalog: (section: string, params: Record<string, unknown>) =>
      ['evaluation', 'catalog', section, params] as const,
  },
} as const;
